import React, { useState, useMemo } from "react";
import { injectIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ScenarioBar from "../../components/ScenarioBar";
import { ROUTES, SCENARIOS, QUIZZES } from "../../utils/constants";
import { getScenarioNumByPath, getTimer } from "../../utils/helpers";
import { updateTime } from "../../redux/actions";
import { postAnswer } from "../../api/answer";
import {
  modalConfirm,
  modalQuestionResolution,
  modalHint,
  modalHintShow
} from "../../utils/logger";
import { IconHandClick } from "@tabler/icons-react";
import CheckButton from "../../components/CheckButton";
import estasseguro from "../../assets/bg/estasseguro-avatar-orange.png";
import respuestacorrecta from "../../assets/bg/correcto-avatar.png";
import respuestaincorrecta from "../../assets/bg/incorrecto-avatar.png";
import btnPistaOrange from "../../assets/btnPistaOrange.png";
import imageHint from "../../assets/hints/imageHintOrange.png";
import imageHintUsed from "../../assets/hintUsed.png";
import unIntento from "../../assets/un-intento-orange.png"
import dosIntentos from "../../assets/dos-intentos-orange.png"

// imágenes para las respuestas
import imgAVCH from "../../assets/tests/scn2/alto-volumen-chaarted.png";
import imgARLAT from "../../assets/tests/scn2/alto-riesgo-latitude.png";
import imgAV from "../../assets/tests/scn2/alto-volumen.png";
import imgAR from "../../assets/tests/scn2/alto-riesgo.png";

function Scenario2Test({ intl }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const group = useSelector((state) => state.group);

  const activeScenario = getScenarioNumByPath(location.pathname);

  const QUIZ_ID = QUIZZES.ALTO_VOLUMEN;
  const SCENARIO_ID = SCENARIOS.SCENARIO_2;

  const [selectedOptions, setSelectedOptions] = useState([]);
  const [isCompleted, setIsCompleted] = useState(false);
  const [hintUsed, setHintUsed] = useState(false);
  const [hintPenalty, setHintPenalty] = useState(0);
  const [hintAppliedToScore, setHintAppliedToScore] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Segundo intento con envío único
  const [attempt, setAttempt] = useState(1);                  // 1..2
  const [blockedOptions, setBlockedOptions] = useState(new Set());
  const [pendingScore, setPendingScore] = useState(0);        // acumulado a enviar al final

  const NUMBERATTEMPTS = 2;
  const CORRECT_ANSWER_POINTS = 10;
  const WRONG_ANSWER_POINTS = -5;
  const CORRECT_ANSWER_POINTS_2 = 3;
  const WRONG_ANSWER_POINTS_2 = 0;

  const options = [
    {
      id: 1,
      text: "Al menos 1 de los siguientes criterios:<ul><li>4 o más metástasis óseas</li><li>(1 fuera de la pélvis/columna)</li><li>Metástasis viscerales</li></ul>",
      img: imgAVCH,
      correct: true,
      explanation: "-----"
    },
    {
      id: 2,
      text: "Al menos 2 de los siguientes criterios:<ul><li>3 o más metástasis óseas</li><li>Metástasis viscerales</li><li>Gleason mayor o igual a 8</li></ul>",
      img: imgARLAT,
      correct: true,
      explanation: "-----"
    },
    {
      id: 3,
      text: "Más de 10 metástasis óseas</br>Metástasis locorregionales",
      img: imgAV,
      correct: false,
      explanation: "-----"
    },
    {
      id: 4,
      text: "PSA mayor o igual a 20</br>Gleason mayor o igual a 8 o T3a</br>PSADT menor o igual a 10 meses",
      img: imgAR,
      correct: false,
      explanation: "-----"
    },
  ];

  const correctIds = options.filter(o => o.correct).map(o => o.id);

  const toggleOption = (id) => {
    if (blockedOptions.has(id)) return; // no permitir seleccionar bloqueadas
    setSelectedOptions(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  async function submitAnswer(finalScore, correctFinal) {
    const totalTime = getTimer();
    dispatch(updateTime(totalTime, 1));

    try {
      const res = await postAnswer({
        scenario: SCENARIO_ID,
        quiz: QUIZ_ID,
        group: group?._id,
        score: finalScore, // único envío con suma final
        time: totalTime,
        lastVisited: ROUTES.SCENARIO2_MUJER,
        correct: correctFinal
      });

      if (res.status === 201) {
        setIsCompleted(true);
        navigate(ROUTES.SCENARIO2_MUJER);
      }
    } catch (error) {
      const status = error?.response?.status;
      const code = error?.response?.data?.code;

      if (status === 409 && code === "E_DUPLICATE_ANSWER") {
        await modalQuestionResolution({
          correct: false,
          question: "Respuesta ya registrada",
          textHtml: `<div class="txt32 txtBlue bold">Esta pregunta ya se había contestado con anterioridad y no se contabilizará, puedes continuar con el resto del juego.</div>`,
          customConfirmButtonHtml: "Ir a la conclusión",
        });
        navigate(ROUTES.SCENARIO2_MUJER);
        return;
      }

      await modalQuestionResolution({
        correct: false,
        question: "No se pudo guardar",
        textHtml: `<div class="txt24 txt-red bold">Ha ocurrido un problema al registrar tu respuesta. Inténtalo de nuevo</div>`,
        customConfirmButtonHtml: "Aceptar",
      });

      setIsSubmitting(false);
    }
  }

  const useHint = async () => {
    const hint = 'Los criterios correctos para definir el volumen y el riesgo fueron establecidos por dos ensayos clínicos pivotales cuyos nombres hacen referencia a características geográficas y astronómicas. Fíjate en las definiciones que incluyen un número concreto de metástasis y la presencia de enfermedad visceral.';
    if (hintUsed) {
      await modalHintShow({ hint, imageHintUsed, color: "orange" });
      return;
    }
    const { used } = await modalHint({ image: imageHint, imageHintUsed, hint, color: "orange" });
    if (used && !hintUsed) {
      setHintUsed(true);
      setHintPenalty(3); // se aplicará una sola vez en el primer intento con puntos
    }
  };

  function checkAnswer() {
    if (selectedOptions.length === 0) {
      modalQuestionResolution({
        correct: false,
        textHtml: `<img src=${estasseguro} alt="¿Estás seguro?" class='estasseguro' /><div class="text-center txtTitulos mt-3 orange">Debes seleccionar las opciones que consideres</div>`,
      });
      return;
    }

    // Correcto si coincide exactamente con el conjunto de correctas
    const isCorrect =
      correctIds.length === selectedOptions.length &&
      correctIds.every((id) => selectedOptions.includes(id));

    modalConfirm({ estasseguro, color: "orange" }).then((res) => {
      if (!res.isConfirmed) return;

      setIsSubmitting(true);
      const hintThisAttempt = (hintUsed && !hintAppliedToScore) ? hintPenalty : 0;

      // ✅ Correcto
      if (isCorrect) {
        const base = (attempt === 1) ? CORRECT_ANSWER_POINTS : CORRECT_ANSWER_POINTS_2;
        const attemptDelta = base - hintThisAttempt;
        const newScore = pendingScore + attemptDelta;
        if (hintThisAttempt) setHintAppliedToScore(true);

        modalQuestionResolution({
          correct: true,
          question: "Tus respuestas",
          textHtml: `<img src=${respuestacorrecta} alt="Respuesta correcta" style='max-width:450px' />
                     <div class="txt32 bold pt-4">Ganaste ${attemptDelta} puntos en este intento${hintThisAttempt ? " (-3 pista)" : ""}</div>
                     ${attempt === 2 ? `<div class="txt20 mt-2">Correcto en el segundo intento</div>` : ""}
                     <div class="txt20 mt-2">Puntuación total de la pregunta: <b>${newScore}</b></div>`,
          customConfirmButtonHtml: "Continuar",
          color: "orange"
        }).then(() => {
          submitAnswer(newScore, true); // único envío final
        });

        return;
      }

      // ❌ Incorrecto
      if (attempt < NUMBERATTEMPTS) {
        // 1er fallo: bloqueamos únicamente las opciones incorrectas que seleccionó
        const wrongSelected = selectedOptions.filter((id) => !correctIds.includes(id));
        const attemptDelta = WRONG_ANSWER_POINTS - hintThisAttempt; // -5 o -8 si pista aquí
        const newScore = pendingScore + attemptDelta;
        if (hintThisAttempt) setHintAppliedToScore(true);

        modalQuestionResolution({
          correct: false,
          question: "Tus respuestas",
          textHtml: `<img src=${respuestaincorrecta} alt="Respuesta incorrecta" style='max-width:450px' />
                     <div class="txt28 bold pt-3">Respuesta incorrecta ${attemptDelta} puntos${hintThisAttempt ? " (incluye -3 por pista)" : ""}</div>
                     <div class="txt22 mt-2">Te queda <b>1</b> intento. Elige de nuevo.</div>
                     <div class="txt20 mt-2">Acumulado hasta ahora: <b>${newScore}</b></div>`,
          customConfirmButtonHtml: "Intentar de nuevo",
          color: "orange"
        }).then(() => {
          setPendingScore(newScore);
          setBlockedOptions(prev => {
            const next = new Set(prev);
            wrongSelected.forEach(id => next.add(id));
            return next;
          });
          setAttempt(attempt + 1);
          setSelectedOptions([]); // empezamos limpio en el 2º intento
          setIsSubmitting(false);
        });

        return;
      }

      // 2º fallo: delta 0 (o -3 si recién aquí aplica pista)
      const attemptDelta = WRONG_ANSWER_POINTS_2 - hintThisAttempt; // 0 o -3
      const newScore = pendingScore + attemptDelta;
      if (hintThisAttempt) setHintAppliedToScore(true);

      modalQuestionResolution({
        correct: false,
        question: "Tus respuestas",
        textHtml: `<img src=${respuestaincorrecta} alt="Respuesta incorrecta" style='max-width:450px' />
                   <div class="txt32 bold pt-4">Has agotado los intentos</div>
                   ${hintThisAttempt ? `<div class="txt20 mt-2">Se aplicó -3 por pista en este intento</div>` : ""}
                   <div class="txt20 mt-2">Puntuación total de la pregunta: <b>${newScore}</b></div>`,
        customConfirmButtonHtml: "Continuar",
        color: "orange"
      }).then(() => {
        submitAnswer(newScore, false); // único envío final
      });
    });
  }

  const remainingAttempts = NUMBERATTEMPTS - attempt + 1;
  
  const shuffledOptions = useMemo(() => {
    return [...options].sort(() => Math.random() - 0.5);
  }, []);

  return (
    <div className="h-100">
      <div className="container-fluid px-5 h-100 d-flex flex-column justify-content-start pt-2 bgCircleOrange">
        <h3 className="txt30 orange bold" style={{ backgroundColor: "#FFFFFFAA", borderRadius: "10px", paddingTop: "5px", paddingBottom: "5px"}}>
          A partir de la información previa: ¿Podrías decir cómo se define enfermedad de alto volumen y de alto riesgo?
        </h3>
        <div className="d-flex justify-content-between">
          <h5 className="mb-4 txt32 text-left my-2 orange">
            <IconHandClick size={40} color="#FF6017" stroke={1} /> Selecciona las respuestas correctas
          </h5>
          <div className="mb-2">
            <img src={remainingAttempts == 2 ? dosIntentos : unIntento} className="imgIntentos" />
          </div>
        </div>
        <div className="row g-4">
          {shuffledOptions.map((option) => {
            const isSelected = selectedOptions.includes(option.id);
            const isBlocked = blockedOptions.has(option.id);
            return (
              <div key={option.id} className="col-md-3">
                <div
                  className={`position-relative cursor-pointer p-1 rounded shadow h-100 ${isSelected ? "selected" : ""} ${isBlocked ? "opacity-75" : ""}`}
                  style={{
                    border: isSelected ? "1px solid #FF601799" : "1px solid #fff",
                    backgroundColor: isSelected ? "#FF601799" : "#fff",
                    pointerEvents: isBlocked ? "none" : "auto",
                  }}
                  onClick={() => toggleOption(option.id)}
                  aria-disabled={isBlocked}
                  title={isBlocked ? "Opción bloqueada (ya fue incorrecta)" : "Seleccionar"}
                >
                  <img
                    src={option.img}
                    alt={`Opción ${option.id}`}
                    style={{ width: "100%", borderRadius: "8px", height: "50%", objectFit: "contain" }}
                  />
                  <div className="txt15 px-1 mt-2" dangerouslySetInnerHTML={{ __html: option.text }} />
                  {isBlocked && <span className="badge bg-secondary position-absolute top-0 end-0 m-2">Bloqueada</span>}
                </div>
              </div>
            );
          })}
        </div>

        <div className="d-flex justify-content-between mt-3">
          <div className="position-relative d-inline-block">
            <img
              src={btnPistaOrange}
              className={`btnPista mt-2 ${hintUsed ? "used" : ""}`}
              onClick={useHint}
              alt={hintUsed ? "Pista usada" : "Usar pista"}
              title={hintUsed ? "Pista usada (-3)" : "Usar pista (-3 puntos)"}
            />
            {hintUsed && <span className="badge-penalty">-3</span>}
          </div>
          <CheckButton
            text="Comprobar respuesta"
            onClick={checkAnswer}
            disabled={isCompleted || isSubmitting}
            color="orange"
            loading={isSubmitting}
          />
        </div>
      </div>

      <ScenarioBar activeScenario={activeScenario} />
    </div>
  );
}

export default injectIntl(Scenario2Test);
